// Sugalarm — cycle 464: Settings → Account → Subscription (phone + web) and
// the kiosk's read-only status card.
//
// Both render ONLY the server's answer (`useSubscription`). The phone / web
// card shows the state in plain words, where it is billed, and the actions
// that source allows on this platform (every rail from `allowedRails`, every
// manage path from `manageActionFor`). The kiosk card shows the state and one
// line, and has NO button, link or QR code: the kiosk never sells.

import { useCallback, useEffect, useState } from "react";

import { mintWebHandoff, openStripePortal, type Subscription } from "../../api/subscription";
import {
  allowedRails,
  billingFailureMessage,
  discardExternalTab,
  getBillingSupport,
  getOffer,
  getStorefront,
  isAllowedBillingUrl,
  KIOSK_SUBSCRIPTION_LINE,
  manageActionFor,
  manageSubscriptions,
  manageTextFor,
  openExternal,
  prepareExternalTab,
  purchase,
  withBillingTimeout,
  restore,
  subscriptionGrants,
  subscriptionSourceLine,
  subscriptionStateLine,
  type NativeOffer,
} from "../../lib/billing";
import {
  applyServerSubscription,
  confirmWithServer,
  currentSubscriptionGeneration,
  refetchSubscription,
  useSubscription,
} from "../../hooks/useSubscription";
import { webLinkLabel } from "../onboarding/SubscriptionStep";
import styles from "./SettingsScreen.module.css";
import card from "./SubscriptionCard.module.css";
import { SubsectionCard } from "./SubsectionCard";

const CARD_TITLE = "Subscription";
const CARD_SUBTITLE = "Your plan and billing.";

/** The kiosk's read-only status card. No controls of any kind. */
export function KioskSubscriptionCard() {
  const { data, loaded } = useSubscription();
  return (
    <SubsectionCard title={CARD_TITLE} subtitle={CARD_SUBTITLE} tone="neutral">
      <div className={card.kioskBody} data-testid="kiosk-subscription-card">
        <p className={card.state}>
          {data ? subscriptionStateLine(data) : loaded ? "Status unavailable" : "Checking..."}
        </p>
        <p className={card.line}>{KIOSK_SUBSCRIPTION_LINE}</p>
      </div>
    </SubsectionCard>
  );
}

type Phase = "idle" | "working" | "confirming";

/** Phone + web: status, source and the allowed actions. */
export function SubscriptionCard({ accountId }: { accountId: string | null }) {
  const { data, loaded, error } = useSubscription();
  const support = getBillingSupport();
  const [storefront, setStorefront] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getStorefront().then((s) => {
      if (!cancelled) setStorefront(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const rails = allowedRails(support.platform, storefront);
  const native = support.platform === "ios" || support.platform === "android";

  // Cycle 465: the STORE's offer decides the button (the free month only
  // when the server and the store agree); loaded once the card can sell.
  const [offer, setOffer] = useState<NativeOffer | null>(null);
  const canSellNatively = native && support.supported;
  const trialEligible = data?.trial_eligible ?? false;
  const priceDisplay = data?.price_display ?? "";
  useEffect(() => {
    if (!canSellNatively || data == null) return;
    let cancelled = false;
    void getOffer({ trialEligible, priceDisplay }).then((o) => {
      if (!cancelled) setOffer(o);
    });
    return () => {
      cancelled = true;
    };
  }, [canSellNatively, data == null, trialEligible, priceDisplay]);

  /** A granting answer from the verify that accepted the transaction IS the
   *  server's state; anything else is polled for as before. */
  const confirm = useCallback(
    async (accepted: Subscription | undefined, startedIn: number) => {
      if (accepted && subscriptionGrants(accepted)) {
        // The server's own answer; dropped if the account changed meanwhile.
        applyServerSubscription(accepted, startedIn);
        void refetchSubscription();
        setPhase("idle");
        return;
      }
      setPhase("confirming");
      const ok = await confirmWithServer(data);
      setPhase("idle");
      if (currentSubscriptionGeneration() !== startedIn) return; // another account's session now
      if (!ok) setNote("We couldn't confirm it yet. If you were charged, it will show here shortly.");
    },
    [data],
  );

  const onSubscribe = useCallback(async () => {
    setNote(null);
    if (data == null) return;
    if (support.platform === "web") {
      const r = await purchase({ productId: "sugaralarm_monthly", accountToken: accountId ?? "" });
      if (!r.ok) setNote("Couldn't open thesugaralarm.com. Try again.");
      return;
    }
    setPhase("working");
    const startedIn = currentSubscriptionGeneration();
    const current =
      offer ?? (await getOffer({ trialEligible: data.trial_eligible, priceDisplay: data.price_display }));
    if (current == null) {
      setPhase("idle");
      setNote(billingFailureMessage(support.supported ? "unavailable" : "unsupported", "purchase"));
      return;
    }
    const r = await purchase({
      productId: current.productId,
      accountToken: accountId ?? "",
      appAccountToken: current.appAccountToken,
      basePlanId: current.basePlanId,
      offerId: current.offerId,
    });
    if (!r.ok) {
      setPhase("idle");
      setNote(billingFailureMessage(r.reason, "purchase"));
      return;
    }
    await confirm(r.subscription, startedIn);
  }, [data, support.platform, support.supported, accountId, offer, confirm]);

  const onRestore = useCallback(async () => {
    setNote(null);
    setPhase("working");
    const startedIn = currentSubscriptionGeneration();
    const r = await restore();
    if (!r.ok) {
      setPhase("idle");
      setNote(billingFailureMessage(r.reason, "restore"));
      return;
    }
    await confirm(r.subscription, startedIn);
  }, [confirm]);

  const onWebLink = useCallback(async () => {
    setNote(null);
    const tab = prepareExternalTab(); // inside the tap, before any await
    setPhase("working");
    const r = await withBillingTimeout(mintWebHandoff());
    setPhase("idle");
    if (r == null || !r.ok || !isAllowedBillingUrl(r.data.url)) {
      discardExternalTab(tab);
      setNote("Couldn't open thesugaralarm.com. Try again.");
      return;
    }
    if (!openExternal(r.data.url, tab).ok) setNote("Couldn't open thesugaralarm.com. Try again.");
  }, []);

  const manage = data ? manageActionFor(data.source, support.platform) : null;

  const onManage = useCallback(async () => {
    setNote(null);
    if (manage === "stripe_portal") {
      const tab = prepareExternalTab(); // inside the tap, before any await
      setPhase("working");
      const r = await withBillingTimeout(openStripePortal());
      setPhase("idle");
      if (r == null || !r.ok || !isAllowedBillingUrl(r.data.portal_url)) {
        discardExternalTab(tab);
        setNote("Couldn't open the billing page. Try again.");
        return;
      }
      if (!openExternal(r.data.portal_url, tab).ok) setNote("Couldn't open the billing page. Try again.");
      return;
    }
    if (manage === "apple_store" || manage === "google_store") {
      const r = await manageSubscriptions(manage);
      if (!r.ok) setNote("Couldn't open the store's subscription page.");
    }
  }, [manage]);

  if (!data) {
    return (
      <SubsectionCard title={CARD_TITLE} subtitle={CARD_SUBTITLE} tone="neutral">
        <p className={card.state}>{loaded && error ? "Status unavailable" : "Checking..."}</p>
      </SubsectionCard>
    );
  }

  const busy = phase !== "idle";
  const canStart = data.state === "none" || data.state === "grace";
  const showStart = canStart && (rails.store != null ? support.supported : support.platform === "web");
  // Cycle 465: the price line under the start button is the STORE's where
  // the store sells (Apple's localized price), the server's static one on
  // the web and until the store answers.
  const startTrial = offer?.hasTrial ?? data.trial_eligible;
  const startPrice = offer?.priceDisplay ?? data.price_display;
  const linkLabel = canStart && rails.webLink ? webLinkLabel(support.platform) : null;
  const source = subscriptionSourceLine(data.source);
  const comped = data.state === "comped";

  return (
    <SubsectionCard title={CARD_TITLE} subtitle={CARD_SUBTITLE} tone="neutral">
      <div className={card.body} data-testid="subscription-card">
        <p className={card.state}>{subscriptionStateLine(data)}</p>
        {!comped && source ? <p className={card.line}>{source}</p> : null}
        {phase === "confirming" ? (
          <p className={card.line} role="status" aria-live="polite">
            Confirming with the store...
          </p>
        ) : null}
        {note ? (
          <p className={card.line} role="alert">
            {note}
          </p>
        ) : null}

        {!comped ? (
          <>
            {manage === "stripe_text" || manage === "google_text" || manage === "apple_text" ? (
              <p className={card.line}>{manageTextFor(manage)}</p>
            ) : null}
            <div className={styles.actionRow}>
              {showStart ? (
                <button
                  type="button"
                  className={styles.btnPrimary}
                  data-billing-control="start"
                  disabled={busy}
                  onClick={() => void onSubscribe()}
                >
                  {startTrial ? "Start free month" : "Subscribe"}
                </button>
              ) : null}
              {native && support.supported ? (
                <button
                  type="button"
                  className={styles.btnSecondary}
                  data-billing-control="restore"
                  disabled={busy}
                  onClick={() => void onRestore()}
                >
                  Restore purchases
                </button>
              ) : null}
              {manage === "stripe_portal" || manage === "apple_store" || manage === "google_store" ? (
                <button
                  type="button"
                  className={styles.btnSecondary}
                  data-billing-control="manage"
                  disabled={busy}
                  onClick={() => void onManage()}
                >
                  Manage subscription
                </button>
              ) : null}
            </div>
            {showStart ? (
              <p className={card.line} data-testid="subscription-card-price">
                {startTrial ? `Then ${startPrice}. Cancel anytime.` : `${startPrice}. Cancel anytime.`}
              </p>
            ) : null}
            {linkLabel ? (
              <button
                type="button"
                className={card.textLink}
                data-billing-control="web-link"
                disabled={busy}
                onClick={() => void onWebLink()}
              >
                {linkLabel}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </SubsectionCard>
  );
}
